import { useId, useRef, useState } from 'react';
import { Location } from '../../types/schema';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import {
    buildInterimLocationDescription,
    getInterimDistrictParentId,
    getInterimLocationDescription,
} from '../../utils/regionLocationHierarchy';
import { Select, type SelectOption } from '../ui';
import { ServiceCenterModal } from '../company-resources/ServiceCenterModal';

interface EditLocationModalProps {
    location: Location;
    companyId: string;
    serviceCenterOptions: SelectOption[];
    serviceCenterId: string;
    onAssignServiceCenter: (location: Location, serviceCenterId: string) => Promise<void>;
    onServiceCentersChanged: () => void;
    onClose: () => void;
}

export const EditLocationModal = ({
    location,
    companyId,
    serviceCenterOptions,
    serviceCenterId: initialServiceCenterId,
    onAssignServiceCenter,
    onServiceCentersChanged,
    onClose,
}: EditLocationModalProps) => {
    const { companyGroupedData, updateLocationInDatabase, refreshRegionAndLocation } =
        useRegionAndLocationContext();
    const fieldId = useId();
    const [name, setName] = useState(location.name);
    const [description, setDescription] = useState(getInterimLocationDescription(location));
    const [regionalDivisionId, setRegionalDivisionId] = useState(location.regionalDivisionId || '');
    const [districtId, setDistrictId] = useState(getInterimDistrictParentId(location) || '');
    const [serviceCenterId, setServiceCenterId] = useState(initialServiceCenterId);
    const [addingServiceCenter, setAddingServiceCenter] = useState(false);
    const [saving, setSaving] = useState(false);
    /** Only dismiss when press + release both land on the backdrop (not after drag-select from a field). */
    const dismissOnClickRef = useRef(false);

    const regionOptions: SelectOption[] = companyGroupedData.map((region) => ({
        value: region.id,
        label: region.name,
    }));
    const districtOptions: SelectOption[] = companyGroupedData
        .filter((region) => !regionalDivisionId || region.id === regionalDivisionId)
        .flatMap((region) =>
            region.districts.map((district) => ({
                value: district.id,
                label: `${district.name} (${region.name})`,
            })),
        );

    const handleSubmit = async () => {
        if (!name.trim() || !regionalDivisionId) return;
        setSaving(true);
        try {
            // Keep district parent only when it still belongs to the selected region.
            const validDistrictId = districtOptions.some((district) => district.value === districtId)
                ? districtId
                : '';
            await updateLocationInDatabase(location.id, {
                name: name.trim(),
                regionalDivisionId,
                description: buildInterimLocationDescription(validDistrictId || null, description),
            });
            await onAssignServiceCenter(location, serviceCenterId);
            await refreshRegionAndLocation();
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const handleServiceCenterCreated = async (saved?: { id: string } | null) => {
        setAddingServiceCenter(false);
        if (saved?.id) {
            setServiceCenterId(saved.id);
        }
        onServiceCentersChanged();
    };

    return (
        <div
            className="modal-overlay"
            onMouseDown={(e) => {
                dismissOnClickRef.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
                if (dismissOnClickRef.current && e.target === e.currentTarget) {
                    onClose();
                }
                dismissOnClickRef.current = false;
            }}
        >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Edit Location</h2>
                    <button type="button" className="modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <div className="modal-field">
                        <label htmlFor={`${fieldId}-name`}>Location Name</label>
                        <input
                            id={`${fieldId}-name`}
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="modal-field">
                        <label htmlFor={`${fieldId}-description`}>Description</label>
                        <textarea
                            id={`${fieldId}-description`}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <div className="modal-field">
                        <label htmlFor={`${fieldId}-region`}>Region</label>
                        <Select
                            inputId={`${fieldId}-region`}
                            ariaLabel="Region"
                            placeholder="Select region"
                            options={regionOptions}
                            value={regionalDivisionId}
                            disabled={saving}
                            onChange={(nextRegionId) => {
                                setRegionalDivisionId(nextRegionId);
                                setDistrictId('');
                            }}
                        />
                    </div>
                    <div className="modal-field">
                        <label htmlFor={`${fieldId}-district`}>District</label>
                        <Select
                            inputId={`${fieldId}-district`}
                            ariaLabel="District"
                            placeholder="Select district"
                            emptyMessage="No districts in this region"
                            options={districtOptions}
                            value={districtId}
                            disabled={saving || !regionalDivisionId}
                            onChange={setDistrictId}
                        />
                        <span className="modal-field-note">
                            Assigning a region/district links this location so it appears under the Region card.
                        </span>
                    </div>
                    <div className="modal-field">
                        <label htmlFor={`${fieldId}-service-center`}>Service Center</label>
                        <Select
                            className="edit-location-service-center"
                            inputId={`${fieldId}-service-center`}
                            ariaLabel={`Service center for ${location.name}`}
                            placeholder="None"
                            emptyMessage="No service centers yet"
                            options={serviceCenterOptions}
                            value={serviceCenterId}
                            disabled={saving}
                            onChange={setServiceCenterId}
                            actionOption={{
                                label: '+ Add service center',
                                onClick: () => setAddingServiceCenter(true),
                            }}
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" className="modal-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="modal-btn-primary"
                        onClick={() => void handleSubmit()}
                        disabled={!name.trim() || !regionalDivisionId || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
            {addingServiceCenter && (
                <ServiceCenterModal
                    isOpen
                    companyId={companyId}
                    editServiceCenter={null}
                    initialCoverage={{
                        regionIds: location.regionalDivisionId ? [location.regionalDivisionId] : [],
                        locationIds: [location.id],
                    }}
                    onClose={() => setAddingServiceCenter(false)}
                    onSave={handleServiceCenterCreated}
                />
            )}
        </div>
    );
};
